import {
    IdcardOutlined,
    DownloadOutlined,
    CheckCircleOutlined,
    SearchOutlined,
    EditOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    DatePicker,
    Row,
    Col,
    Input,
    Table,
    Select,
    message,
    Modal,
    Tag,
} from "antd";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

export default function AttendanceHead() {
    const { authData, backUrl } = useContext(AuthContext);
    const isMobile = window.innerWidth < 768;

    // State management
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isPayroll, setIsPayroll] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filter state
    const [filters, setFilters] = useState({
        emp_code: "",
        emp_name: "",
        organisation: "",
        department: "",
        designation: "",
        from_date: null,
        to_date: null,
        status: "",
    });

    // Dropdown options
    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // Permission Modal state
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Status Change Modal state
    const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
    const [statusChangeRecord, setStatusChangeRecord] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [statusRemarks, setStatusRemarks] = useState("");
    const [statusSubmitting, setStatusSubmitting] = useState(false);

    // Available status options
    const statusOptions = [
        { value: "PRESENT", label: "Present", color: "green" },
        { value: "PERMISSION", label: "Permission", color: "orange" },
        { value: "ABSENT", label: "Absent", color: "red" },
        { value: "HOLIDAY", label: "Holiday", color: "blue" },
        { value: "Weekoff", label: "Week Off", color: "purple" },
        { value: "COMPOFF", label: "Comp Off", color: "cyan" },
        { value: "Casual Leave", label: "Casual Leave", color: "magenta" },
        { value: "Sick Leave", label: "Sick Leave", color: "volcano" },
        { value: "Half Day Leave", label: "Half Day", color: "geekblue" },
        { value: "LOP", label: "LOP", color: "geekblue" },
    ];

    // Check if user is payroll
    useEffect(() => {
        if (authData?.employee?.user?.username === "BCPL569") {
            setIsPayroll(true);
        }
    }, [authData]);

    // Build API URL with query parameters
    const buildApiUrl = () => {
        const params = new URLSearchParams();

        // Add pagination parameters
        params.append('page', currentPage);
        params.append('page_size', pageSize);

        // Add filter parameters
        if (filters.emp_code?.trim()) {
            params.append('emp_code', filters.emp_code.trim());
        }
        if (filters.emp_name?.trim()) {
            params.append('emp_name', filters.emp_name.trim());
        }
        if (filters.organisation) {
            params.append('organisation', filters.organisation);
        }
        if (filters.department) {
            params.append('department', filters.department);
        }
        if (filters.designation) {
            params.append('designation', filters.designation);
        }
        if (filters.from_date) {
            const fromDate = dayjs(filters.from_date).startOf('day');
            params.append('from_date', fromDate.format('YYYY-MM-DD'));
        }
        if (filters.to_date) {
            const toDate = dayjs(filters.to_date).endOf('day');
            params.append('to_date', toDate.format('YYYY-MM-DD'));
        }
        if (filters.status) {
            params.append('status', filters.status);
        }

        return `${backUrl}/api/hr/attendance-headoffice/?${params.toString()}`;
    };

    // Fetch attendance data from backend
    const fetchData = async () => {
        setLoading(true);
        try {
            const url = buildApiUrl();
            console.log("Fetching data from:", url);
            // Clear existing data first
            setDataSource([]);
            setSelectedRowKeys([]);

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            console.log("Backend Response:", res.data);

            // Set new data from backend
            const data = res.data.results || res.data;
            console.log("Setting data:", data);
            console.log("Total count:", res.data.count);

            setDataSource(data);
            setTotalRecords(res.data.count || data.length);
        } catch (error) {
            console.error("Error fetching attendance data:", error);
            message.error("Failed to fetch attendance data");
            // Clear data on error
            setDataSource([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // Fetch organizations
    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganisations(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch organizations:", err);
        }
    };

    // Fetch departments based on organization
    const fetchDepartments = async (organisationId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${organisationId}`
            );
            setDepartments(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch departments:", err);
        }
    };

    // Fetch designations based on department
    const fetchDesignations = async (departmentId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${departmentId}`
            );
            setDesignations(res.data.results || res.data);
        } catch (err) {
            console.error("Error fetching designations", err);
        }
    };

    // Initial load
    useEffect(() => {
        fetchOrganizations();
        fetchData();
    }, []);

    // Only fetch when pagination changes
    useEffect(() => {
        if (currentPage === 1) {
            fetchData();
        }
    }, [filters]);

    // Handle filter updates
    const updateFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Handle organisation change
    const handleOrganisationChange = (value) => {
        setFilters({
            ...filters,
            organisation: value,
            department: "",
            designation: "",
        });
        setDepartments([]);
        setDesignations([]);
        if (value) {
            fetchDepartments(value);
        }
    };

    // Handle department change
    const handleDepartmentChange = (value) => {
        setFilters({
            ...filters,
            department: value,
            designation: "",
        });
        setDesignations([]);
        if (value) {
            fetchDesignations(value);
        }
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setFilters({
                ...filters,
                from_date: dates[0],
                to_date: dates[1],
            });
        } else {
            setFilters({
                ...filters,
                from_date: null,
                to_date: null,
            });
        }
    };

    // Apply filters manually
    const handleApplyFilters = () => {
        console.log("Applying filters:", filters);
        setCurrentPage(1); // Reset to first page
        setSelectedRowKeys([]); // Clear selections
        fetchData();
    };

    // Clear all filters
    const clearFilters = () => {
        console.log("Clearing all filters");

        const clearedFilters = {
            emp_code: "",
            emp_name: "",
            organisation: "",
            department: "",
            designation: "",
            from_date: null,
            to_date: null,
            status: "",
        };

        setFilters(clearedFilters);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        setSelectedRowKeys([]);

        // Build URL with cleared filters immediately
        const params = new URLSearchParams();
        params.append('page', 1);
        params.append('page_size', pageSize);

        const url = `${backUrl}/api/hr/attendance-headoffice/?${params.toString()}`;

        // Fetch with cleared filters
        setLoading(true);
        axios.get(url, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        })
            .then(res => {
                const data = res.data.results || res.data;
                setDataSource(data);
                setTotalRecords(res.data.count || data.length);
            })
            .catch(error => {
                console.error("Error fetching attendance data:", error);
                message.error("Failed to fetch attendance data");
                setDataSource([]);
                setTotalRecords(0);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Handle status edit click
    const handleStatusEditClick = (record) => {
        setStatusChangeRecord(record);
        setNewStatus(record.AttendanceStatus);
        setStatusRemarks("");
        setIsStatusModalVisible(true);
    };

    // Submit status change request
    const handleSubmitStatusChange = async () => {
        if (!statusChangeRecord) return;

        if (!newStatus) {
            message.warning("Please select a status");
            return;
        }

        if (!statusRemarks.trim()) {
            message.warning("Please enter remarks before submitting");
            return;
        }

        setStatusSubmitting(true);
        try {
            const payload = {
                attendance_id: statusChangeRecord.id,
                new_status: newStatus,
                remarks: statusRemarks.trim(),
            };

            await axios.post(
                `${backUrl}/api/hr/attendance-changes/raise-request/`,
                payload,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            message.success("Status change request submitted successfully!");
            setIsStatusModalVisible(false);
            setStatusRemarks("");
            setNewStatus("");
            setStatusChangeRecord(null);
            fetchData();
        } catch (error) {
            console.error("Error submitting status change:", error);

            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Failed to submit status change request";

            message.error(errorMessage);
        } finally {
            setStatusSubmitting(false);
        }
    };

    // Handle status modal cancel
    const handleStatusModalCancel = () => {
        setIsStatusModalVisible(false);
        setStatusRemarks("");
        setNewStatus("");
        setStatusChangeRecord(null);
    };

    // Handle permission consideration click
    const handlePermissionClick = (record) => {
        setSelectedRecord(record);
        setRemarks("");
        setIsModalVisible(true);
    };

    // Submit permission consideration
    const handleSubmitPermission = async () => {
        if (!selectedRecord) return;

        if (!remarks.trim()) {
            message.warning("Please enter remarks before submitting");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                attendance_id: selectedRecord.id,
                employee: selectedRecord.Emp_id,
                remarks: remarks,
            };

            await axios.post(
                `${backUrl}/api/hr/attendance-requests/`,
                payload,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            message.success("Permission consideration submitted successfully!");
            setIsModalVisible(false);
            setRemarks("");
            setSelectedRecord(null);
            fetchData();
        } catch (error) {
            console.error("Error submitting permission:", error);

            const errorMessage =
                error.response?.data?.data ||
                error.response?.data?.message ||
                "Failed to submit permission consideration";

            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle modal cancel
    const handleModalCancel = () => {
        setIsModalVisible(false);
        setRemarks("");
        setSelectedRecord(null);
    };

    // Handle table pagination change
    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    // Download Excel
    const handleDownloadExcel = () => {
        const rowsToExport =
            selectedRowKeys.length > 0
                ? dataSource.filter((item) =>
                    selectedRowKeys.includes(item.id || item.EmployeeCode)
                )
                : dataSource;

        if (rowsToExport.length === 0) {
            message.warning("No data to export!");
            return;
        }

        const excelData = rowsToExport.map((row) => {
            const rowData = {};
            columns
                .filter((col) => col.key !== "action")
                .forEach((col) => {
                    if (col.dataIndex === "check_in_photo_url" ||
                        col.dataIndex === "check_out_photo_url") {
                        rowData[col.title] = row[col.dataIndex] ? "Photo Available" : "-";
                    } else if (col.dataIndex === "CheckInLocation" ||
                        col.dataIndex === "CheckOutLocation") {
                        rowData[col.title] = row[col.dataIndex] || "-";
                    } else {
                        rowData[col.title] = row[col.dataIndex] ?? "-";
                    }
                });

            return rowData;
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Head Office Attendance");
        XLSX.writeFile(workbook, "HeadOffice_Attendance.xlsx");

        message.success("Download started!");
    };

    // Get status color
    const getStatusColor = (status) => {
        const statusOption = statusOptions.find(opt => opt.value === status);
        return statusOption ? statusOption.color : "default";
    };

    // Table columns definition
    const columns = [
        { title: "Employee Code", dataIndex: "EmployeeCode", key: "EmployeeCode" },
        { title: "Employee Name", dataIndex: "EmployeeName", key: "EmployeeName" },
        { title: "Company Name", dataIndex: "Organisation", key: "Organisation" },
        { title: "Department", dataIndex: "Department", key: "Department" },
        { title: "Designation", dataIndex: "Designation", key: "Designation" },
        { title: "Attendance Date", dataIndex: "Date", key: "Date" },
        { title: "Work Location", dataIndex: "WorkLocation", key: "WorkLocation" },
        { title: "Check-In", dataIndex: "CheckIn", key: "CheckIn" },
        {
            title: "Check-In Photo",
            dataIndex: "check_in_photo_url",
            key: "check_in_photo_url",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Check-In Location",
            dataIndex: "CheckInLocation",
            key: "CheckInLocation",
            render: (text, record) =>
                record.check_in_latitude && record.check_in_longitude ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_in_latitude},${record.check_in_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: "Check In Site",
            dataIndex: "CheckInWorkSite",
            render: (v, record) => (
                <>
                    <p>{v || "-"}</p>
                    {record.onduty_type_check_in}
                </>
            ),
        },
        { title: "Punch-In", dataIndex: "PunchIn", key: "PunchIn" },
        { title: "Check-Out", dataIndex: "CheckOut", key: "CheckOut" },
        {
            title: "Check-Out Photo",
            dataIndex: "check_out_photo_url",
            key: "check_out_photo_url",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-Out"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Check-Out Location",
            dataIndex: "CheckOutLocation",
            key: "CheckOutLocation",
            render: (text, record) =>
                record.check_out_latitude && record.check_out_longitude ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_out_latitude},${record.check_out_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: "Check Out Site",
            dataIndex: "CheckOutWorkSite",
            render: (v, record) => (
                <>
                    <p>{v || "-"}</p>
                    {record.onduty_type_check_out}
                </>
            ),
        },
        { title: "Punch-Out", dataIndex: "PunchOut", key: "PunchOut" },
        { title: "First In Type", dataIndex: "FirstInType", key: "FirstInType" },
        { title: "Last Out Type", dataIndex: "LastOutType", key: "LastOutType" },
        {
            title: "Original Status",
            dataIndex: "AttendanceStatus",
            key: "AttendanceStatus",
            width: 200,
            // pending_status_change
            // status_change_remarks
        },
        {
            title: "Status",
            dataIndex: "AttendanceStatus",
            key: "AttendanceStatus",
            width: 200,
            render: (v, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                    <Tag color={getStatusColor(v)}>
                        {v === "Sunday" ? "Week Off" : v === "Present" ? "Original Status - Permission, Changed as Present" : v}
                    </Tag>
                    {
                        isPayroll && (
                            <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleStatusEditClick(record)}
                                style={{ padding: '4px 8px' }}
                                title="Edit Status"
                            />
                        )
                    }
                </div>
            )
        },
        { title: "Total Time", dataIndex: "TotalTime", key: "TotalTime" },
        // ...(isPayroll
        //     ? [
        //         {
        //             title: "Action",
        //             key: "action",
        //             width: 250,
        //             render: (_, record) => {
        //                 if (record.AttendanceStatus === "Permission") {
        //                     const permissions = record.permissions || [];
        //                     const autoPermission = permissions.find(
        //                         (p) => p.type === "Auto-Deduction"
        //                     );

        //                     const isAutoPermission = record.AttendanceStatus === "Permission" && !!autoPermission;

        //                     return (
        //                         <>
        //                             {
        //                                 isAutoPermission ? (
        //                                     <Button
        //                                         type="primary"
        //                                         icon={<CheckCircleOutlined />}
        //                                         onClick={() => handlePermissionClick(record)}
        //                                         size="small"
        //                                         style={{ backgroundColor: "#54ec08ff", borderColor: "#54ec08ff" }}
        //                                     >
        //                                         Permission
        //                                     </Button>
        //                                 ) : (
        //                                     <p>
        //                                         -
        //                                     </p>
        //                                 )
        //                             }
        //                         </>

        //                     );
        //                 }
        //                 else {
        //                     return (
        //                         <p>
        //                             -
        //                         </p>)
        //                 }
        //             },
        //         },
        //     ]
        //     : []),
    ]

    return (
        <section>
            <center>
                {isMobile ? (
                    <p style={{ fontSize: "15px", textTransform: "uppercase", color: "#0e497b" }}>
                        <IdcardOutlined /> Head Office Attendance Records
                    </p>
                ) : (
                    <h5>
                        <IdcardOutlined /> Head Office Attendance Records
                    </h5>
                )}
            </center>

            {/* Filters Card */}
            <Card
                style={{
                    margin: "20px",
                    padding: "20px",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Employee Code</label>
                        <Input
                            placeholder="Enter Employee Code"
                            value={filters.emp_code}
                            onChange={(e) => updateFilter('emp_code', e.target.value)}
                            allowClear
                            onPressEnter={handleApplyFilters}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Employee Name</label>
                        <Input
                            placeholder="Enter Employee Name"
                            value={filters.emp_name}
                            onChange={(e) => updateFilter('emp_name', e.target.value)}
                            allowClear
                            onPressEnter={handleApplyFilters}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Company Name</label>
                        <Select
                            placeholder="Select Company"
                            allowClear
                            style={{ width: "100%" }}
                            value={filters.organisation || undefined}
                            onChange={handleOrganisationChange}
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {organisations.map((org) => (
                                <Option key={org.id} value={org.id}>
                                    {org.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Department</label>
                        <Select
                            showSearch
                            placeholder="Select Department"
                            allowClear
                            style={{ width: "100%" }}
                            value={filters.department || undefined}
                            onChange={handleDepartmentChange}
                            disabled={!filters.organisation}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {departments.map((dept) => (
                                <Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Designation</label>
                        <Select
                            showSearch
                            placeholder="Select Designation"
                            allowClear
                            style={{ width: "100%" }}
                            value={filters.designation || undefined}
                            onChange={(value) => updateFilter('designation', value)}
                            disabled={!filters.department}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {designations.map((des) => (
                                <Option key={des.id} value={des.id}>
                                    {des.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Attendance Status</label>
                        <Select
                            placeholder="Select Status"
                            allowClear
                            style={{ width: "100%" }}
                            value={filters.status || undefined}
                            onChange={(value) => updateFilter('status', value)}
                        >
                            <Option value="PRESENT">Present</Option>
                            <Option value="PERMISSION">Permission</Option>
                            <Option value="ABSENT">Absent</Option>
                            <Option value="HOLIDAY">Holiday</Option>
                            <Option value="Weekoff">Week Off</Option>
                            <Option value="Compoff">Comp Off</Option>
                            <Option value="CASUAL LEAVE">Casual Leave</Option>
                            <Option value="SICK LEAVE">Sick Leave</Option>
                            <Option value="HALFDAY_LEAVE">Half Day</Option>
                            <Option value="LOP">LOP</Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={10} lg={6}>
                        <label>Date Range</label>
                        <RangePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={
                                filters.from_date && filters.to_date
                                    ? [dayjs(filters.from_date), dayjs(filters.to_date)]
                                    : null
                            }
                            onChange={handleDateRangeChange}
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={3}>
                        <label style={{ visibility: 'hidden' }}>Action</label>
                        <Button
                            block
                            onClick={clearFilters}
                            style={{ width: "100%" }}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Download Button */}
            <div style={{ margin: "20px", textAlign: "center" }}>
                <Button
                    icon={<DownloadOutlined />}
                    style={{
                        height: "36px",
                        width: "220px",
                        backgroundColor: "#0e497b",
                        color: "white",
                    }}
                    onClick={handleDownloadExcel}
                    disabled={dataSource.length === 0}
                >
                    Download {selectedRowKeys.length > 0 ? 'Selected' : 'All'}
                </Button>
            </div>

            {/* Attendance Table */}
            <div style={{ margin: "20px" }}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    bordered
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    scroll={{ x: "max-content" }}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                        preserveSelectedRowKeys: true,
                    }}
                    rowKey={(record) => record.id || record.EmployeeCode}
                    onChange={handleTableChange}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </div>

            {/* Permission Consideration Modal */}
            <Modal
                title="Permission Consideration"
                open={isModalVisible}
                onOk={handleSubmitPermission}
                onCancel={handleModalCancel}
                confirmLoading={submitting}
                okText="Submit"
                cancelText="Cancel"
                width={600}
            >
                {selectedRecord && (
                    <div style={{ marginBottom: "20px" }}>
                        <p>
                            <strong>Employee:</strong> {selectedRecord.EmployeeName} (
                            {selectedRecord.EmployeeCode})
                        </p>
                        <p>
                            <strong>Date:</strong> {selectedRecord.Date}
                        </p>
                        <p>
                            <strong>Department:</strong> {selectedRecord.Department}
                        </p>
                    </div>
                )}
                <div>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                        Remarks:
                    </label>
                    <TextArea
                        rows={4}
                        placeholder="Enter your remarks here..."
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        maxLength={500}
                        showCount
                    />
                </div>
            </Modal>

            {/* Status Change Modal */}
            <Modal
                title="Change Attendance Status"
                open={isStatusModalVisible}
                onOk={handleSubmitStatusChange}
                onCancel={handleStatusModalCancel}
                confirmLoading={statusSubmitting}
                okText="Submit Request"
                cancelText="Cancel"
                width={600}
            >
                {statusChangeRecord && (
                    <div style={{ marginBottom: "20px" }}>
                        <p>
                            <strong>Employee:</strong> {statusChangeRecord.EmployeeName} (
                            {statusChangeRecord.EmployeeCode})
                        </p>
                        <p>
                            <strong>Date:</strong> {statusChangeRecord.Date}
                        </p>
                        <p>
                            <strong>Department:</strong> {statusChangeRecord.Department}
                        </p>
                        <p>
                            <strong>Current Status:</strong>{" "}
                            <Tag color={getStatusColor(statusChangeRecord.AttendanceStatus)}>
                                {statusChangeRecord.AttendanceStatus === "Sunday"
                                    ? "Week Off"
                                    : statusChangeRecord.AttendanceStatus}
                            </Tag>
                        </p>
                    </div>
                )}
                <div style={{ marginBottom: "20px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                        New Status: <span style={{ color: "red" }}>*</span>
                    </label>
                    <Select
                        placeholder="Select new status"
                        style={{ width: "100%" }}
                        value={newStatus}
                        onChange={(value) => setNewStatus(value)}
                    >
                        {statusOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                                <p>{option.label}</p>
                            </Option>
                        ))}
                    </Select>
                </div>
                <div>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                        Remarks: <span style={{ color: "red" }}>*</span>
                    </label>
                    <TextArea
                        rows={4}
                        placeholder="Enter reason for status change..."
                        value={statusRemarks}
                        onChange={(e) => setStatusRemarks(e.target.value)}
                        maxLength={500}
                        showCount
                    />
                </div>
            </Modal>
        </section>
    );
}